import { useCallback, useEffect, useMemo, useState, } from 'react';
import { useTimeout } from "./useTimeout.js";
export const defaultActivityStopTimeout = 1000;
// ------
// Topics
/**
 * Listen for broadcasted events given a room and topic.
 *
 * @see https://instantdb.com/docs/presence-and-topics
 * @example
 *  function App({ roomId }) {
 *    const room = db.room('chats', roomId);
 *    db.rooms.useTopicEffect(room, 'emoji', (message, peer) => {
 *      console.log(peer.name, 'sent', message);
 *    });
 *    // ...
 *  }
 */
export function useTopicEffect(room, topic, onEvent) {
    useEffect(() => {
        const unsub = room.core._reactor.subscribeTopic(room.id, topic, (event, peer) => {
            onEvent(event, peer);
        });
        return unsub;
    }, [room.id, topic]);
}
/**
 * Broadcast an event to a room.
 *
 * @see https://instantdb.com/docs/presence-and-topics
 * @example
 * function App({ roomId }) {
 *   const room = db.room('chat', roomId);
 *   const publishTopic = db.rooms.usePublishTopic(room, "emoji");
 *
 *   return (
 *     <button onClick={() => publishTopic({ emoji: "🔥" })}>Send emoji</button>
 *   );
 * }
 *
 */
export function usePublishTopic(room, topic) {
    useEffect(() => room.core._reactor.joinRoom(room.id), [room.id]);
    const publishTopic = useCallback((data) => {
        room.core._reactor.publishTopic({
            roomType: room.type,
            roomId: room.id,
            topic,
            data,
        });
    }, [room.id, topic]);
    return publishTopic;
}
// ---------
// Presence
/**
 * Listen for peer's presence data in a room, and publish the current user's presence.
 *
 * @see https://instantdb.com/docs/presence-and-topics
 * @example
 *  function App({ roomId }) {
 *    const {
 *      peers,
 *      publishPresence
 *    } = db.room(roomType, roomId).usePresence({ keys: ["name", "avatar"] });
 *
 *    // ...
 *  }
 */
export function usePresence(room, opts = {}) {
    var _a, _b;
    const [state, setState] = useState(() => {
        var _a;
        return ((_a = room.core._reactor.getPresence(room.type, room.id, opts)) !== null && _a !== void 0 ? _a : {
            peers: {},
            isLoading: true,
        });
    });
    useEffect(() => {
        const unsub = room.core._reactor.subscribePresence(room.type, room.id, opts, (data) => {
            setState(data);
        });
        return unsub;
    }, [room.id, opts.user, (_a = opts.peers) === null || _a === void 0 ? void 0 : _a.join(), (_b = opts.keys) === null || _b === void 0 ? void 0 : _b.join()]);
    const publishPresence = useCallback((data) => {
        room.core._reactor.publishPresence(room.type, room.id, data);
    }, [room.type, room.id]);
    const ret = useMemo(() => {
        return Object.assign(Object.assign({}, state), { publishPresence });
    }, [state, publishPresence]);
    return ret;
}
/**
 * Publishes presence data to a room
 *
 * @see https://instantdb.com/docs/presence-and-topics
 * @example
 *  function App({ roomId, nickname }) {
 *    const room = db.room('chat', roomId);
 *    db.rooms.useSyncPresence(room, { nickname });
 *  }
 */
export function useSyncPresence(room, data, deps) {
    useEffect(() => room.core._reactor.joinRoom(room.id, data), [room.id]);
    useEffect(() => {
        return room.core._reactor.publishPresence(room.type, room.id, data);
    }, [room.type, room.id, deps !== null && deps !== void 0 ? deps : JSON.stringify(data)]);
}
// -----------------
// Typing Indicator
/**
 * Manage typing indicator state
 *
 * @see https://instantdb.com/docs/presence-and-topics
 * @example
 *  function App({ roomId }) {
 *    const room = db.room('chat', roomId);
 *    const {
 *      active,
 *      setActive,
 *      inputProps,
 *    } = db.rooms.useTypingIndicator(room, "chat-input");
 *
 *    return <input {...inputProps} />;
 *  }
 */
export function useTypingIndicator(room, inputName, opts = {}) {
    const timeout = useTimeout();
    const observedPresence = rooms.usePresence(room, {
        keys: [inputName],
    });
    const active = useMemo(() => {
        var _a;
        const presenceSnapshot = room._core._reactor.getPresence(room.type, room.id);
        return (opts === null || opts === void 0 ? void 0 : opts.writeOnly)
            ? []
            : Object.values((_a = presenceSnapshot === null || presenceSnapshot === void 0 ? void 0 : presenceSnapshot.peers) !== null && _a !== void 0 ? _a : {}).filter((p) => p[inputName] === true);
    }, [opts === null || opts === void 0 ? void 0 : opts.writeOnly, observedPresence]);
    const setActive = useCallback((isActive) => {
        var _a;
        room.core._reactor.publishPresence(room.type, room.id, {
            [inputName]: isActive,
        });
        if (!isActive)
            return;
        if ((opts === null || opts === void 0 ? void 0 : opts.timeout) === null || (opts === null || opts === void 0 ? void 0 : opts.timeout) === 0)
            return;
        timeout.set((_a = opts === null || opts === void 0 ? void 0 : opts.timeout) !== null && _a !== void 0 ? _a : defaultActivityStopTimeout, () => {
            room.core._reactor.publishPresence(room.type, room.id, {
                [inputName]: null,
            });
        });
    }, [room.type, room.id, inputName, opts === null || opts === void 0 ? void 0 : opts.timeout, timeout]);
    const onKeyDown = useCallback((e) => {
        const isEnter = (opts === null || opts === void 0 ? void 0 : opts.stopOnEnter) && e.key === 'Enter';
        const isActive = !isEnter;
        setActive(isActive);
    }, [opts.stopOnEnter, setActive]);
    const onBlur = useCallback(() => {
        setActive(false);
    }, [setActive]);
    const inputProps = useMemo(() => {
        return { onKeyDown, onBlur };
    }, [onKeyDown, onBlur]);
    return {
        active,
        setActive,
        inputProps,
    };
}
// --------------
// Hooks
export const rooms = {
    useTopicEffect,
    usePublishTopic,
    usePresence,
    useSyncPresence,
    useTypingIndicator,
};
// ------------
// Class
export class InstantReactRoom {
    constructor(core, type, id) {
        /**
         * @deprecated
         * `db.room(...).useTopicEffect` is deprecated. You can replace it with `db.rooms.useTopicEffect`.
         *
         * @example
         *
         * // Before
         * const room = db.room('chat', 'room-id');
         * room.useTopicEffect('emoji', (message, peer) => {  });
         *
         * // After
         * const room = db.room('chat', 'room-id');
         * db.rooms.useTopicEffect(room, 'emoji', (message, peer) => {  });
         */
        this.useTopicEffect = (topic, onEvent) => {
            rooms.useTopicEffect(this, topic, onEvent);
        };
        /**
         * @deprecated
         * `db.room(...).usePublishTopic` is deprecated. You can replace it with `db.rooms.usePublishTopic`.
         *
         * @example
         *
         * // Before
         * const room = db.room('chat', 'room-id');
         * const publish = room.usePublishTopic('emoji');
         *
         * // After
         * const room = db.room('chat', 'room-id');
         * const publish = db.rooms.usePublishTopic(room, 'emoji');
         */
        this.usePublishTopic = (topic) => {
            return rooms.usePublishTopic(this, topic);
        };
        /**
         * @deprecated
         * `db.room(...).usePresence` is deprecated. You can replace it with `db.rooms.usePresence`.
         *
         * @example
         *
         * // Before
         * const room = db.room('chat', 'room-id');
         * const { peers } = room.usePresence({ keys: ["name", "avatar"] });
         *
         * // After
         * const room = db.room('chat', 'room-id');
         * const { peers } = db.rooms.usePresence(room, { keys: ["name", "avatar"] });
         */
        this.usePresence = (opts = {}) => {
            return rooms.usePresence(this, opts);
        };
        /**
         * @deprecated
         * `db.room(...).useSyncPresence` is deprecated. You can replace it with `db.rooms.useSyncPresence`.
         *
         * @example
         *
         * // Before
         * const room = db.room('chat', 'room-id');
         * room.useSyncPresence(room, { nickname });
         *
         * // After
         * const room = db.room('chat', 'room-id');
         * db.rooms.useSyncPresence(room, { nickname });
         */
        this.useSyncPresence = (data, deps) => {
            return rooms.useSyncPresence(this, data, deps);
        };
        /**
         * @deprecated
         * `db.room(...).useTypingIndicator` is deprecated. You can replace it with `db.rooms.useTypingIndicator`.
         *
         * @example
         *
         * // Before
         * const room = db.room('chat', 'room-id');
         * const typing = room.useTypingIndiactor(room, 'chat-input');
         *
         * // After
         * const room = db.room('chat', 'room-id');
         * const typing = db.rooms.useTypingIndiactor(room, 'chat-input');
         */
        this.useTypingIndicator = (inputName, opts = {}) => {
            return rooms.useTypingIndicator(this, inputName, opts);
        };
        this.core = core;
        this._core = this.core;
        this.type = type;
        this.id = id;
    }
}
//# sourceMappingURL=InstantReactRoom.js.map